import type { UseNextSanityImageProps } from 'next-sanity-image'
import { useNextSanityImage } from 'next-sanity-image'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useMemo } from 'react'
import { z } from 'zod'

import Button from '@nxt-insurance/ui-kit/components/Button'
import * as ProductCard from '@nxt-insurance/ui-kit/components/cards/ProductCard/index.web'
import type {
  BorderRadius,
  CardElementVariant,
} from '@nxt-insurance/ui-kit/components/cards/ProductCard/types'
import {
  BORDER_RADIUS_OPTIONS,
  CARD_ELEMENTS_VARIANTS,
} from '@nxt-insurance/ui-kit/components/cards/ProductCard/types'

import { runQuery, sanityClientLike } from '@/cms/client'
import { colorEnum, spaceEnum } from '@/cms/helperTypes'
import { imageSchema } from '@/cms/image'
import { webUrlSchema, websiteQuery } from '@/cms/webUrl'
import { Icon } from '@/components/Icon'
import { EVENTS } from '@/constants/analytics'
import { useAnalytics } from '@/context/AnalyticsContext'
import { useUrlFromWebUrlReference } from '@/hooks/useUrlFromWebUrlReference'
import { useWebUrlRel } from '@/hooks/useWebUrlRel'
import { getLocaleParts } from '@/utils/locale'
import { getPathWithParams, removeImpermanentSearchParams } from '@/utils/url'
import type { PortableTextTypeComponentProps } from '@portabletext/react'
import { useQuery } from '@tanstack/react-query'

import { BlockContent } from './BlockContent'

const CARD_WIDTH = 327

const variantEnum = z.enum(
  Object.keys(CARD_ELEMENTS_VARIANTS) as [CardElementVariant, ...CardElementVariant[]]
)
const radii = z.union(
  BORDER_RADIUS_OPTIONS.map((el): z.ZodLiteral<BorderRadius> => z.literal(el)) as [
    z.ZodLiteral<BorderRadius>,
    z.ZodLiteral<BorderRadius>,
    ...z.ZodLiteral<BorderRadius>[],
  ]
)

const productCardSchema = z.object({
  _type: z.literal('productCard'),
  cardVariant: variantEnum.nullable().optional(),
  tagsVariant: variantEnum.optional(),
  image: imageSchema.optional(),
  borderRadius: radii,
  borderColor: colorEnum.optional(),
  coverBorderRadius: radii.optional(),
  maxCardWidth: z.number().nullable().optional(),
  headerTopPadding: spaceEnum.optional(),
  href: z
    .object({
      _type: z.literal('reference'),
      _ref: z.string(),
    })
    .optional(),
  title: z.string(),
  subtitle: z.string().optional(),
  tags: z.array(z.string()).optional(),
  imageAlt: z.string().optional(),
  content: z.array(z.unknown()).optional(),
  primaryFooterCtaHref: webUrlSchema.optional(),
  primaryFooterCtaLabel: z.string().optional(),
  secondaryFooterCtaHref: webUrlSchema.optional(),
  secondaryFooterCtaLabel: z.string().optional(),
})

type ProductCardSchema = z.infer<typeof productCardSchema>

const Footer = (
  props: Pick<
    ProductCardSchema,
    | 'primaryFooterCtaHref'
    | 'primaryFooterCtaLabel'
    | 'secondaryFooterCtaHref'
    | 'secondaryFooterCtaLabel'
  > & { sectionName: string }
) => {
  const {
    primaryFooterCtaLabel,
    primaryFooterCtaHref,
    secondaryFooterCtaLabel,
    secondaryFooterCtaHref,
  } = props
  const primaryHref = useUrlFromWebUrlReference(primaryFooterCtaHref, { optional: true })
  const secondaryHref = useUrlFromWebUrlReference(secondaryFooterCtaHref, { optional: true })
  const relPrimary = useWebUrlRel(primaryFooterCtaHref)
  const relSecondary = useWebUrlRel(secondaryFooterCtaHref)

  const { track } = useAnalytics()

  if (!primaryHref || !primaryFooterCtaLabel) {
    return null
  }
  return (
    <ProductCard.Footer>
      {secondaryFooterCtaLabel && secondaryHref ? (
        <Link className="group w-full" href={secondaryHref} rel={relSecondary}>
          <Button
            variantColor="uiLight"
            label={secondaryFooterCtaLabel}
            onPress={() => {
              track(EVENTS.ButtonClicked, {
                label: secondaryFooterCtaLabel,
                href: secondaryHref,
                sectionName: props.sectionName,
              })
            }}
          />
        </Link>
      ) : null}
      <Link className="group w-full" href={primaryHref} rel={relPrimary}>
        <Button
          label={primaryFooterCtaLabel}
          onPress={() => {
            track(EVENTS.ButtonClicked, {
              label: primaryFooterCtaLabel,
              href: primaryHref,
              sectionName: props.sectionName,
            })
          }}
        />
      </Link>
    </ProductCard.Footer>
  )
}

const Card = (props: {
  sectionName: string
  card: ProductCardSchema
  image: UseNextSanityImageProps | null
  width?: number
}) => (
  <ProductCard.Root
    variant={props.card.cardVariant ?? undefined}
    borderRadius={props.card.borderRadius}
    maxWidth={props.card.maxCardWidth ?? props.width ?? CARD_WIDTH}
    borderColor={props.card.borderColor ?? undefined}
  >
    {props.image ? (
      <ProductCard.Cover borderRadius={props.card.coverBorderRadius ?? props.card.borderRadius}>
        {props.card.tags ? (
          <ProductCard.TagList>
            {props.card.tags.map((tag, index) => (
              <ProductCard.Tag
                key={index}
                text={tag}
                variant={props.card.tagsVariant ?? 'brandLight'}
              />
            ))}
          </ProductCard.TagList>
        ) : null}
        <ProductCard.CoverImage {...props.image} alt={props.card.imageAlt ?? ''} />
      </ProductCard.Cover>
    ) : null}
    <ProductCard.Header paddingTop={props.card.headerTopPadding ?? undefined}>
      <ProductCard.HeaderTextContainer>
        <ProductCard.Title>{props.card.title}</ProductCard.Title>
        {props.card.subtitle ? (
          <ProductCard.Subtitle>{props.card.subtitle}</ProductCard.Subtitle>
        ) : null}
      </ProductCard.HeaderTextContainer>
      {props.card.href ? <Icon size="small" icon={{ name: 'action.arrowRight_24' }} /> : null}
    </ProductCard.Header>
    {props.card.content ? (
      <ProductCard.Content>
        <BlockContent
          blocks={props.card.content}
          width={props.card.maxCardWidth ?? CARD_WIDTH}
          sectionName={props.sectionName}
        />
      </ProductCard.Content>
    ) : null}
    <Footer
      sectionName={props.sectionName}
      primaryFooterCtaHref={props.card.primaryFooterCtaHref}
      primaryFooterCtaLabel={props.card.primaryFooterCtaLabel}
      secondaryFooterCtaHref={props.card.secondaryFooterCtaHref}
      secondaryFooterCtaLabel={props.card.secondaryFooterCtaLabel}
    />
  </ProductCard.Root>
)

export const ProductCardRenderer = (props: {
  node: PortableTextTypeComponentProps<unknown>
  sectionName: string
  width?: number
}) => {
  const { query, locale } = useRouter()
  const card = useMemo(() => productCardSchema.parse(props.node.value), [props.node.value])
  const imageProps = useNextSanityImage(sanityClientLike, card.image ?? null, {
    imageBuilder: (imageUrlBuilder) => imageUrlBuilder.width(CARD_WIDTH * 2),
  })
  const { data: parsedHref } = useQuery({
    queryKey: ['website.route', card.href?._ref],
    queryFn: async () => {
      if (!card.href) {
        return null
      }
      const [lang] = getLocaleParts(locale)
      const route = await runQuery(websiteQuery, { lang, id: card.href._ref })
      const whitelistedParams = removeImpermanentSearchParams(query).toString()
      const { folder, slug } = route
      const url = [folder, slug.replace('index', '')].filter(Boolean).join('/')
      return getPathWithParams(`/${url}`, { params: whitelistedParams })
    },
  })

  if (parsedHref) {
    return (
      <Link
        className="w-full group-hover:scale-110"
        style={{ maxWidth: card.maxCardWidth ?? props.width ?? CARD_WIDTH }}
        href={parsedHref}
      >
        <Card card={card} width={props.width} image={imageProps} sectionName={props.sectionName} />
      </Link>
    )
  }
  return <Card card={card} width={props.width} image={imageProps} sectionName={props.sectionName} />
}
